import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Trash2, Inbox, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { formatDate } from '../lib/format';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const EMAIL_STATUS_STYLES = {
  sent: 'bg-[#24135F]/5 text-[#24135F] border-[#24135F]/20',
  queued: 'bg-[#D9D9D6]/25 text-[#53565A] border-[#53565A]/25',
  failed: 'bg-[#E4002B]/5 text-[#E4002B] border-[#E4002B]/25',
  pending: 'bg-[#D9D9D6]/15 text-[#53565A] border-[#D9D9D6]',
};

export default function Enquiries() {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => {
    api
      .get('/admin/enquiries')
      .then((res) => setEnquiries(res.data.enquiries))
      .catch(() => toast.error('Failed to load enquiries'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/enquiries/${deleteTarget.id}`);
      toast.success('Enquiry deleted');
      setDeleteTarget(null);
      load();
    } catch {
      toast.error('Failed to delete enquiry');
    }
  };

  return (
    <div className="space-y-5">
      <h1 className="font-heading text-3xl tracking-wide text-[#24135F]">ENQUIRIES</h1>

      <div className="bg-white border border-[#D9D9D6] rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-5 space-y-3">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-14 rounded-lg" />
            ))}
          </div>
        ) : enquiries.length === 0 ? (
          <div className="text-center py-16" data-testid="admin-enquiries-empty">
            <Inbox className="h-10 w-10 text-[#D9D9D6] mx-auto mb-3" />
            <p className="text-sm text-[#53565A]">No enquiries yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="admin-enquiries-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {enquiries.map((e) => (
                  <TableRow
                    key={e.id}
                    data-testid="admin-enquiry-row"
                    className="hover:bg-[#D9D9D6]/15 cursor-pointer"
                    onClick={() => setViewing(e)}
                  >
                    <TableCell className="text-sm text-[#53565A] whitespace-nowrap">{formatDate(e.created_at)}</TableCell>
                    <TableCell className="text-sm font-medium">{e.name}</TableCell>
                    <TableCell>
                      <p className="text-xs text-[#53565A]">{e.email}</p>
                      {e.phone && <p className="text-xs text-[#53565A]/70">{e.phone}</p>}
                    </TableCell>
                    <TableCell className="text-sm">
                      {e.vehicle_title ? (
                        <span className="text-[#E4002B] font-medium text-xs">{e.vehicle_title}</span>
                      ) : (
                        <span className="text-[#53565A]/70 text-xs">General enquiry</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-[#53565A] max-w-[220px] truncate">{e.message}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[10px] ${EMAIL_STATUS_STYLES[e.email_status] || EMAIL_STATUS_STYLES.pending}`}>
                        {e.email_status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right" onClick={(ev) => ev.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="icon"
                        data-testid="admin-enquiry-delete-button"
                        onClick={() => setDeleteTarget(e)}
                        className="h-8 w-8 hover:bg-[#E4002B]/5"
                      >
                        <Trash2 className="h-4 w-4 text-[#E4002B]" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* View enquiry dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent data-testid="enquiry-detail-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-wider">ENQUIRY DETAILS</DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs text-[#53565A]/70 font-semibold uppercase">Customer</p>
                  <p className="font-medium mt-0.5">{viewing.name}</p>
                </div>
                <div>
                  <p className="text-xs text-[#53565A]/70 font-semibold uppercase">Date</p>
                  <p className="font-medium mt-0.5">{formatDate(viewing.created_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-[#53565A]/70 font-semibold uppercase">Email</p>
                  <a href={`mailto:${viewing.email}`} className="text-[#E4002B] font-medium mt-0.5 block hover:underline">
                    {viewing.email}
                  </a>
                </div>
                <div>
                  <p className="text-xs text-[#53565A]/70 font-semibold uppercase">Phone</p>
                  <p className="font-medium mt-0.5">{viewing.phone || '-'}</p>
                </div>
              </div>
              {viewing.vehicle_title && (
                <div>
                  <p className="text-xs text-[#53565A]/70 font-semibold uppercase">Vehicle</p>
                  {viewing.vehicle_slug ? (
                    <Link
                      to={`/trucks/${viewing.vehicle_slug}`}
                      target="_blank"
                      className="text-[#E4002B] font-medium mt-0.5 inline-flex items-center gap-1 hover:underline"
                    >
                      {viewing.vehicle_title} <ExternalLink className="h-3 w-3" />
                    </Link>
                  ) : (
                    <p className="font-medium mt-0.5">{viewing.vehicle_title}</p>
                  )}
                </div>
              )}
              <div>
                <p className="text-xs text-[#53565A]/70 font-semibold uppercase mb-1">Message</p>
                <div className="bg-[#D9D9D6]/15 border border-[#D9D9D6] rounded-lg p-4 text-sm leading-relaxed whitespace-pre-wrap">
                  {viewing.message}
                </div>
              </div>
              <Button
                asChild
                className="w-full rounded-full bg-[#E4002B] text-white hover:bg-[#E4002B]/90 h-11 font-bold tracking-wider"
              >
                <a href={`mailto:${viewing.email}?subject=Re: Your enquiry${viewing.vehicle_title ? ` about ${viewing.vehicle_title}` : ''}`}>
                  <Mail className="h-4 w-4 mr-2" /> REPLY BY EMAIL
                </a>
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete enquiry?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the enquiry from {deleteTarget?.name}. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-enquiry-confirm-button"
              onClick={handleDelete}
              className="bg-[#E4002B] hover:bg-[#E4002B]/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
